import { api } from '../lib/api-client.js';

import type {
  AssignWorkOrderInput,
  AttachmentConfirmInput,
  AttachmentPresignInput,
  BulkTransitionInput,
  CommentInput,
  CreateWorkOrderInput,
  TransitionWorkOrderInput,
  UpdateWorkOrderInput,
  WorkOrderFiltersQuery,
  WorkOrderSeverity,
  WorkOrderStatus,
} from '@sitewise/shared';

export interface WorkOrderSummary {
  id: string;
  orgId: string;
  number: number;
  displayNumber: string;
  buildingId: string;
  buildingName?: string;
  unitId: string | null;
  title: string;
  description: string | null;
  category: string;
  severity: WorkOrderSeverity;
  status: WorkOrderStatus;
  assignedTeamId: string | null;
  assignedUserId: string | null;
  assignedVendorId: string | null;
  dueAt: string | null;
  submittedBy: string | null;
  submittedVia: string;
  sourceInspectionId: string | null;
  sourceViolationId: string | null;
  sourceComplianceId: string | null;
  parentWorkOrderId: string | null;
  completedAt: string | null;
  completedBy: string | null;
  completionNotes: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  metadata: Record<string, unknown>;
  aiSuggestedCategory?: string | null;
  aiSuggestedSeverity?: WorkOrderSeverity | null;
  aiSuggestedAt?: string | null;
  aiSuggestionRationale?: string | null;
  aiSuggestionConfidence?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkOrderEventRow {
  id: string;
  workOrderId: string;
  kind: string;
  actorUserId: string | null;
  actorKind: string;
  body: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface WorkOrderAttachmentRow {
  id: string;
  workOrderId: string;
  eventId: string | null;
  uploadId: string;
  kind: string;
  storageKey: string;
  originalFilename: string;
  contentType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  caption: string | null;
  uploadedByUserId: string | null;
  uploadedByKind: string;
  transcript?: string | null;
  transcribedAt?: string | null;
  createdAt: string;
  downloadUrl: string | null;
  downloadExpiresAt: string | null;
}

export interface WorkOrderUserSummary {
  id: string;
  name: string;
  email: string;
}

export interface WorkOrderListResponse {
  workOrders: WorkOrderSummary[];
  nextCursor: string | null;
  statusCounts: Record<WorkOrderStatus, number>;
}

export interface WorkOrderDetailResponse {
  workOrder: WorkOrderSummary;
  building: { id: string; name: string; addressLine1: string } | null;
  unit: { id: string; unitNumber: string } | null;
  events: WorkOrderEventRow[];
  attachments: WorkOrderAttachmentRow[];
  users: Record<string, WorkOrderUserSummary>;
  vendor: { id: string; name: string; trade: string } | null;
  pendingVendorSignoffs: Array<{
    id: string;
    signedAt: string;
    vendorUserId: string;
    vendorCompanyId: string;
    completionNotes: string | null;
  }>;
}

export async function acknowledgeVendorSignoff(workOrderId: string, signoffId: string) {
  await api(`/work-orders/${workOrderId}/vendor-signoffs/${signoffId}/acknowledge`, {
    method: 'POST',
  });
}

export interface WorkOrderStatsResponse {
  open: number;
  overdue: number;
  dueSoon: number;
  upcoming7d: number;
  byStatus: Record<WorkOrderStatus, number>;
  bySeverity: Record<WorkOrderSeverity, number>;
}

export interface WorkOrderDashboardRow extends WorkOrderSummary {
  buildingName: string;
}

export interface WorkOrderDashboardResponse {
  stats: WorkOrderStatsResponse;
  mostOverdue: WorkOrderDashboardRow[];
  upcoming7d: WorkOrderDashboardRow[];
  dueSoon: WorkOrderDashboardRow[];
}

function qs(params: Record<string, string | number | boolean | string[] | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length > 0) search.set(k, v.join(','));
    } else {
      search.set(k, String(v));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export async function listWorkOrders(
  filters: Partial<WorkOrderFiltersQuery> & { mine?: boolean } = {},
): Promise<WorkOrderListResponse> {
  return api(
    `/work-orders${qs(filters as Record<string, string | string[] | number | boolean | undefined>)}`,
  );
}

export async function fetchWorkOrder(id: string): Promise<WorkOrderDetailResponse> {
  return api(`/work-orders/${id}`);
}

export async function createWorkOrder(input: CreateWorkOrderInput) {
  return api<{ workOrder: WorkOrderSummary; events: WorkOrderEventRow[] }>('/work-orders', {
    method: 'POST',
    body: input,
  });
}

export async function updateWorkOrder(id: string, input: UpdateWorkOrderInput) {
  return api<{ workOrder: WorkOrderSummary; events: WorkOrderEventRow[] }>(`/work-orders/${id}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function transitionWorkOrder(id: string, input: TransitionWorkOrderInput) {
  return api<{ workOrder: WorkOrderSummary; event: WorkOrderEventRow }>(
    `/work-orders/${id}/transition`,
    { method: 'POST', body: input },
  );
}

export async function commentOnWorkOrder(id: string, input: CommentInput) {
  return api<{ event: WorkOrderEventRow }>(`/work-orders/${id}/comments`, {
    method: 'POST',
    body: input,
  });
}

export async function assignWorkOrder(id: string, input: AssignWorkOrderInput) {
  return api<{ workOrder: WorkOrderSummary; event: WorkOrderEventRow }>(
    `/work-orders/${id}/assign`,
    { method: 'POST', body: input },
  );
}

export async function bulkTransitionWorkOrders(input: BulkTransitionInput) {
  return api<{ workOrders: WorkOrderSummary[]; transitioned: number }>(
    '/work-orders/bulk/transition',
    { method: 'POST', body: input },
  );
}

export async function getWorkOrderStats(args: { mine?: boolean; buildingId?: string } = {}) {
  return api<WorkOrderStatsResponse>(
    `/work-orders/stats${qs({ mine: args.mine ? 'true' : undefined, buildingId: args.buildingId })}`,
  );
}

export async function fetchWorkOrderDashboard() {
  return api<WorkOrderDashboardResponse>('/work-orders/dashboard');
}

export async function presignWorkOrderAttachment(
  workOrderId: string,
  input: AttachmentPresignInput,
) {
  return api<{
    attachment: WorkOrderAttachmentRow;
    uploadId: string;
    uploadUrl: string;
    objectKey: string;
    headers: Record<string, string>;
    expiresAt: string;
  }>(`/work-orders/${workOrderId}/attachments`, {
    method: 'POST',
    body: input,
  });
}

export async function confirmWorkOrderAttachment(
  workOrderId: string,
  attachmentId: string,
  input: AttachmentConfirmInput = {},
) {
  return api<{ attachment: WorkOrderAttachmentRow }>(
    `/work-orders/${workOrderId}/attachments/${attachmentId}/confirm`,
    { method: 'POST', body: input },
  );
}

export async function deleteWorkOrderAttachment(workOrderId: string, attachmentId: string) {
  await api(`/work-orders/${workOrderId}/attachments/${attachmentId}`, { method: 'DELETE' });
}

export async function deleteWorkOrder(id: string) {
  await api(`/work-orders/${id}`, { method: 'DELETE' });
}
